import {
  deleteDoc,
  deleteField,
  FieldPath,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { teamSharedCollectionDoc } from '../../../firebase/collections';
import type { CollectionEntry, CollectionVisibility } from '../../../types';
import { readCollectionEntries } from '../../../utils/collectionBag';
import {
  TEAM_PROJECTION_VERSION,
  type StoredTeamSharedCollection,
  type TeamSharedCollection,
  type TeamSharedCollectionRow,
} from '../types';

/**
 * A map is bulkier on the wire than tuples — roughly 30 bytes per card against
 * 20 — so the guard is checked against the shape actually written. The largest
 * real collection is ~1,100 cards, well inside this.
 */
const MAX_PROJECTION_BYTES = 900_000;

/** Firestore caps a write at 500 field transforms; stay well under it. */
const MAX_PATCH_CARDS = 200;

export function collectionEntriesToCompact(
  entries: Record<string, CollectionEntry> | CollectionEntry[]
): Array<[string, number, number]> {
  if (Array.isArray(entries)) {
    return entries
      .filter((e) => e.quantity > 0 && Boolean(e.cardId))
      .map((e) => [e.cardId, e.quantity, e.quantityFoil ?? 0] as [string, number, number]);
  }
  // Prefer map keys as cardId — Firestore collection docs omit cardId in the payload.
  return Object.entries(entries)
    .filter(([, e]) => e.quantity > 0)
    .map(([cardId, e]) => [cardId, e.quantity, e.quantityFoil ?? 0] as [string, number, number]);
}

/** Collection entries → the stored map shape. */
export function collectionEntriesToProjectionMap(
  entries: Record<string, CollectionEntry> | CollectionEntry[]
): Record<string, TeamSharedCollectionRow> {
  const out: Record<string, TeamSharedCollectionRow> = {};
  for (const [cardId, quantity, quantityFoil] of collectionEntriesToCompact(entries)) {
    if (!cardId || quantity <= 0) continue;
    out[cardId] = { q: quantity, f: Math.min(Math.max(0, quantityFoil), quantity) };
  }
  return out;
}

/**
 * Read either stored shape as a map.
 *
 * Call this exactly once, where `snap.data()` is cast. Consumers guard on
 * shape (`buildOwnership` reads `entries` as a keyed map), so an unnormalized
 * legacy document would silently render as "this member owns nothing".
 */
export function normalizeSharedProjection(
  data: StoredTeamSharedCollection | null | undefined,
): TeamSharedCollection | null {
  if (!data) return null;
  const raw = data.entries;
  if (!Array.isArray(raw)) {
    return { ...data, entries: raw ?? {} } as TeamSharedCollection;
  }

  const entries: Record<string, TeamSharedCollectionRow> = {};
  for (const row of raw) {
    if (!Array.isArray(row)) continue;
    const [cardId, quantity, quantityFoil] = row;
    if (typeof cardId !== 'string' || !cardId) continue;
    const q = Number(quantity) || 0;
    if (q <= 0) continue;
    const prior = entries[cardId];
    const f = Math.max(0, Number(quantityFoil) || 0);
    if (prior) {
      prior.q += q;
      prior.f = Math.min(prior.f + f, prior.q);
    } else {
      entries[cardId] = { q, f: Math.min(f, q) };
    }
  }
  return { ...data, entries } as TeamSharedCollection;
}

/** True when the stored document is the current map shape. */
export function isCurrentProjectionShape(
  data: StoredTeamSharedCollection | null | undefined,
): boolean {
  return Boolean(data) && data!.v === TEAM_PROJECTION_VERSION && !Array.isArray(data!.entries);
}

/** True when collection visibility alone implies teammates can see cards. */
export function visibilitySharesWithTeam(visibility: CollectionVisibility): boolean {
  return visibility === 'team' || visibility === 'public';
}

/**
 * Effective member share flag: public/team visibility always shares with the team.
 * The per-member checkbox cannot hide an already-public collection from teammates.
 */
export function effectiveShareCollection(
  visibility: CollectionVisibility,
  shareCollection: boolean
): boolean {
  return shareCollection || visibilitySharesWithTeam(visibility);
}

/**
 * Whether to write/keep a sharedCollections projection.
 * Public/team visibility always projects — a stale shareCollection=false must not hide them.
 * Private never projects (opt-in share bumps visibility to team on join).
 */
export function shouldProjectToTeam(
  visibility: CollectionVisibility,
  shareCollection: boolean
): boolean {
  void shareCollection;
  return visibilitySharesWithTeam(visibility);
}

/** Firestore rejects `undefined` field values — omit optional keys instead. */
function buildProjectionPayload(opts: {
  uid: string;
  displayName?: string;
  slug?: string;
  entries: Record<string, TeamSharedCollectionRow>;
  revision?: number;
}): TeamSharedCollection {
  const payload: TeamSharedCollection = {
    uid: opts.uid,
    v: TEAM_PROJECTION_VERSION,
    entries: opts.entries,
    updatedAt: Timestamp.now(),
  };
  const name = opts.displayName?.trim();
  if (name) payload.displayName = name;
  const slug = opts.slug?.trim();
  if (slug) payload.slug = slug;
  if (typeof opts.revision === 'number') payload.revision = opts.revision;
  return payload;
}

/**
 * What we last wrote for `${teamId}/${uid}`, so the next sync can send only
 * what moved. Absent means "shape unknown" and forces a full rewrite, which is
 * what makes a fresh tab, a legacy tuple document, and a failed patch all
 * converge on the same safe path.
 */
const lastProjected = new Map<string, Record<string, TeamSharedCollectionRow>>();

function projectionKey(teamId: string, uid: string): string {
  return `${teamId}/${uid}`;
}

/** Test seam — session state must not leak between cases. */
export function resetProjectionSyncState(): void {
  lastProjected.clear();
}

/** Cards whose stored row would change, plus those that should be removed. */
export function diffProjectionMaps(
  previous: Record<string, TeamSharedCollectionRow>,
  next: Record<string, TeamSharedCollectionRow>,
): { changed: string[]; removed: string[] } {
  const changed: string[] = [];
  const removed: string[] = [];
  for (const [cardId, row] of Object.entries(next)) {
    const prior = previous[cardId];
    if (!prior || prior.q !== row.q || prior.f !== row.f) changed.push(cardId);
  }
  for (const cardId of Object.keys(previous)) {
    if (!(cardId in next)) removed.push(cardId);
  }
  return { changed, removed };
}

/**
 * Update varargs for a set of card changes.
 *
 * Card ids contain `/`, so dotted paths like `entries.${cardId}` are rejected —
 * the segments must go through `FieldPath`.
 */
function projectionPatchFields(
  entries: Record<string, TeamSharedCollectionRow>,
  changed: string[],
  removed: string[],
  revision?: number,
): Array<string | FieldPath | unknown> {
  const fields: Array<string | FieldPath | unknown> = [
    'v',
    TEAM_PROJECTION_VERSION,
    'updatedAt',
    Timestamp.now(),
  ];
  if (typeof revision === 'number') fields.push('revision', revision);
  for (const cardId of changed) {
    fields.push(new FieldPath('entries', cardId), entries[cardId]);
  }
  for (const cardId of removed) {
    fields.push(new FieldPath('entries', cardId), deleteField());
  }
  return fields;
}

/**
 * Write the member's shared projection.
 *
 * Patches only the cards that moved when we know the stored document is
 * already the current shape and we know what is in it; otherwise rewrites it
 * whole. A burst of card adds used to re-upload the member's entire
 * collection every 800 ms — tens of kilobytes to say that one quantity went
 * from 2 to 3.
 */
export async function syncTeamSharedCollection(opts: {
  teamId: string;
  uid: string;
  displayName?: string | null;
  slug?: string | null;
  entries: Record<string, CollectionEntry>;
  visibility: CollectionVisibility;
  shareCollection: boolean;
  revision?: number;
}): Promise<void> {
  const ref = teamSharedCollectionDoc(opts.teamId, opts.uid);
  const key = projectionKey(opts.teamId, opts.uid);

  if (!shouldProjectToTeam(opts.visibility, opts.shareCollection)) {
    lastProjected.delete(key);
    try {
      await deleteDoc(ref);
    } catch {
      // ignore missing
    }
    return;
  }

  const nextEntries = collectionEntriesToProjectionMap(opts.entries);
  const previous = lastProjected.get(key);

  if (previous) {
    const { changed, removed } = diffProjectionMaps(previous, nextEntries);
    if (changed.length === 0 && removed.length === 0) return;
    if (changed.length + removed.length <= MAX_PATCH_CARDS) {
      const fields = projectionPatchFields(nextEntries, changed, removed, opts.revision);
      const [first, second, ...rest] = fields;
      try {
        await updateDoc(ref, first as string | FieldPath, second, ...rest);
        lastProjected.set(key, nextEntries);
        return;
      } catch (err) {
        // The document may have been deleted, or rewritten by another tab.
        // Fall through to a full rewrite rather than leaving it half-patched.
        lastProjected.delete(key);
        console.warn('[teamSharedCollectionSync] patch failed, rewriting', err);
      }
    }
  }

  const payload = buildProjectionPayload({
    uid: opts.uid,
    displayName: opts.displayName ?? undefined,
    slug: opts.slug ?? undefined,
    entries: nextEntries,
    revision: opts.revision,
  });

  const approx = JSON.stringify(payload).length;
  if (approx > MAX_PROJECTION_BYTES) {
    throw new Error('Collection is too large to share with the team.');
  }

  await setDoc(ref, payload);
  lastProjected.set(key, nextEntries);
}

export async function clearTeamSharedCollection(teamId: string, uid: string): Promise<void> {
  lastProjected.delete(projectionKey(teamId, uid));
  try {
    await deleteDoc(teamSharedCollectionDoc(teamId, uid));
  } catch {
    // ignore
  }
}

export interface ViewerCollectionOverlay {
  uid: string;
  displayName?: string | null;
  slug?: string | null;
  entries: Record<string, CollectionEntry>;
}

/**
 * Overlay the signed-in member's live collection onto team ownership rows.
 * Used when public/team visibility should share, even if the Firestore
 * sharedCollections projection is missing or stale.
 */
export function mergeViewerCollectionIntoOwnership<
  T extends {
    cardId: string;
    totalQty: number;
    totalFoil: number;
    byMember: Array<{
      uid: string;
      displayName: string;
      slug?: string;
      quantity: number;
      quantityFoil: number;
    }>;
  },
>(rows: T[], viewer: ViewerCollectionOverlay | null | undefined): T[] {
  if (!viewer) return rows;
  const compact = collectionEntriesToCompact(viewer.entries);
  if (compact.length === 0) return rows;

  const map = new Map<string, T>();
  for (const row of rows) {
    map.set(row.cardId, {
      ...row,
      byMember: row.byMember.map((m) => ({ ...m })),
    });
  }

  const name = viewer.displayName?.trim() || 'You';
  const slug = viewer.slug?.trim() || undefined;

  for (const [cardId, quantity, quantityFoil] of compact) {
    if (!cardId || !quantity) continue;
    let row = map.get(cardId);
    if (!row) {
      row = {
        cardId,
        totalQty: 0,
        totalFoil: 0,
        byMember: [],
      } as unknown as T;
      map.set(cardId, row);
    }
    const existing = row.byMember.find((m) => m.uid === viewer.uid);
    if (existing) {
      row.totalQty += quantity - existing.quantity;
      row.totalFoil += quantityFoil - existing.quantityFoil;
      existing.quantity = quantity;
      existing.quantityFoil = quantityFoil;
      existing.displayName = name;
      if (slug) existing.slug = slug;
    } else {
      row.totalQty += quantity;
      row.totalFoil += quantityFoil;
      row.byMember.push({
        uid: viewer.uid,
        displayName: name,
        slug,
        quantity,
        quantityFoil,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => a.cardId.localeCompare(b.cardId));
}

/** Load personal collection map for sync after privacy changes. */
export async function loadUserCollectionMap(uid: string): Promise<Record<string, CollectionEntry>> {
  return readCollectionEntries(uid);
}
